import React, { useState } from 'react';
import { QuickEntryModal } from './QuickEntryModal';

interface ActionButton {
  label: string;
  icon: string;
  color: string;
  action: () => void;
}

interface QuickActionsTileProps {
  onNavigate?: (view: string) => void;
  onExpand?: () => void;
}

export const QuickActionsTile: React.FC<QuickActionsTileProps> = ({ onNavigate, onExpand }) => {
  const [showQuickEntry, setShowQuickEntry] = useState(false);

  const actions: ActionButton[] = [
    {
      label: 'Log Metrics',
      icon: '📊',
      color: 'var(--accent-blue)',
      action: () => setShowQuickEntry(true)
    },
    {
      label: 'Log Food',
      icon: '🍎',
      color: 'var(--accent-green)',
      action: () => onNavigate?.('nutrition')
    },
    {
      label: 'View Goals',
      icon: '🎯',
      color: 'var(--accent-orange)',
      action: () => onNavigate?.('goals')
    },
    {
      label: 'Add Workout',
      icon: '💪',
      color: 'var(--accent-purple)',
      action: () => onNavigate?.('health-metrics')
    }
  ];

  return (
    <div
      className="quick-actions-tile"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '12px',
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-medium)',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div>
          <h3 style={{
            fontSize: '14px',
            fontWeight: '600',
            margin: '0',
            color: 'var(--text-primary)'
          }}>
            Quick Actions
          </h3>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            marginTop: '2px'
          }}>
            Common tasks
          </div>
        </div>
        {onExpand && (
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '12px'
          }}>
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
        )}
      </div>

      {/* Actions Grid */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '8px'
      }}>
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation(); // Prevent tile drag when clicking action
              action.action();
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              backgroundColor: 'var(--bg-secondary)',
              border: `1px solid var(--border-color)`,
              borderRadius: 'var(--radius-small)',
              padding: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '10px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.borderColor = action.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'var(--border-color)';
            }}
          >
            {/* Background Gradient */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${action.color}15, ${action.color}05)`,
                opacity: 0,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0';
              }}
            />
            
            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                fontSize: '16px',
                marginBottom: '2px'
              }}>
                {action.icon}
              </div>
              <div style={{
                fontSize: '9px',
                fontWeight: '500',
                textAlign: 'center',
                lineHeight: '1.2'
              }}>
                {action.label}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick Stats */}
      <div style={{
        marginTop: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '8px',
        color: 'var(--text-secondary)'
      }}>
        <div>
          <span style={{ fontWeight: '500' }}>Today:</span> 3 actions
        </div>
        <div>
          <span style={{ fontWeight: '500' }}>Week:</span> 18 actions
        </div>
      </div>

      {/* Quick Entry Modal */}
      <QuickEntryModal
        isOpen={showQuickEntry}
        onClose={() => setShowQuickEntry(false)}
        onSave={() => {
          // Refresh dashboard data
          window.location.reload();
        }}
      />
    </div>
  );
};